import { ArticleImage } from "@babackapp/customer-ui/components/article";
import { getExchangeVariantDisplay } from "@babackapp/return-widget-components/utils/exchange-variant-display";
import type { LineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import type {
  DraftLineItem,
  Order,
} from "@babackapp/return-widget-components/types";
import { getExchangedArticle } from "@babackapp/return-variables-registry";
import { ArrowRightIcon } from "lucide-react";

type ExchangeArticle = {
  /** Purchased options, then the chosen ones: "38 → 39". */
  variantDisplay: string | null;
  /** Set only when the customer gets another product with a photo to show. */
  exchangeTitle: string | null;
  /** Set only when it shows something else than the purchased photo. */
  exchangeImage: string | null;
  exchangeSku: string | null;
};

/**
 * What an exchanged row shows next to the purchased article. Another colour
 * often keeps the product title (one product per colour), so the product id,
 * else a variant photo, tells the two apart (`getExchangedArticle`, shared
 * with the back office and the emails, title included). The portal reads the
 * catalog, so it knows whether the chosen photo is the variant's own or the
 * product's.
 */
export function getExchangeArticle(
  order: Order | null,
  item: DraftLineItem,
  original: Pick<
    LineItemDisplay,
    "title" | "subtitle" | "image" | "sku" | "productId" | "variantId"
  >,
): ExchangeArticle {
  const exchange =
    item.resolution === "exchange" && item.exchangeVariantId
      ? getExchangeVariantDisplay(
          order,
          item.lineItemId,
          item.exchangeVariantId,
        )
      : null;
  const label = exchange?.label ?? null;
  const exchangedArticle = getExchangedArticle({
    resolution: item.resolution,
    original: {
      variantId: original.variantId,
      productId: original.productId,
      image: original.image,
      title: original.title,
    },
    exchanged: exchange && {
      variantId: item.exchangeVariantId,
      productId: exchange.productId,
      image: exchange.image,
      isVariantImage: exchange.isVariantImage,
      productTitle: exchange.productTitle,
    },
  });

  return {
    variantDisplay:
      label && original.subtitle
        ? `${original.subtitle} → ${label}`
        : (label ?? original.subtitle),
    exchangeTitle: exchangedArticle?.title ?? null,
    exchangeImage: exchangedArticle?.image ?? null,
    exchangeSku:
      exchange?.sku && exchange.sku !== original.sku ? exchange.sku : null,
  };
}

type ExchangeArticleImageProps = {
  src: string | null;
  exchangeSrc: string | null;
  exchangeAlt: string;
  /** Shared with the earlier steps: the purchased photo keeps travelling. */
  layoutId: string;
};

/** Purchased photo, then the exchanged one behind an arrow when they differ. */
export function ExchangeArticleImage({
  src,
  exchangeSrc,
  exchangeAlt,
  layoutId,
}: ExchangeArticleImageProps) {
  if (!exchangeSrc) {
    return <ArticleImage src={src ?? undefined} layoutId={layoutId} />;
  }
  return (
    <div className="col-start-1 row-span-3 row-start-1 flex items-start gap-1">
      <ArticleImage src={src ?? undefined} layoutId={layoutId} />
      <ArrowRightIcon
        aria-hidden
        className="mt-4 size-3 shrink-0 text-muted-foreground"
      />
      <ArticleImage
        src={exchangeSrc}
        alt={exchangeAlt}
        layoutId={`${layoutId}-exchange`}
      />
    </div>
  );
}
